import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Logo } from '@/components/generals/logo'
import { LINKS } from '@/constants/kinematic'

type NavItem = {
    label: string
    href: string
    external?: boolean
}

const NAV_ITEMS: NavItem[] = [
    { label: 'Games', href: '#games' },
    { label: 'Theming', href: '#theming' },
    { label: 'Server', href: '#server' },
    { label: 'Fairness', href: '#fairness' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Docs', href: LINKS.docs, external: true }
]

const externalProps = { target: '_blank', rel: 'noreferrer' }

const SiteNav: FC = () => {
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 16)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const onResize = () => {
            if (window.innerWidth >= 1024) setOpen(false)
        }

        window.addEventListener('keydown', onKey)
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const solid = scrolled || open

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-5'>
            <nav
                aria-label='Primary'
                className={cn(
                    'mx-auto flex h-14 max-w-[1320px] items-center justify-between gap-4 rounded-2xl border pl-3.5 pr-2 transition-[background-color,border-color,box-shadow] duration-300',
                    solid ? 'border-white/10 bg-ink/80 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.95)] backdrop-blur-xl' : 'border-transparent bg-transparent'
                )}
            >
                <a href='#hero' onClick={() => setOpen(false)} className='rounded-lg'>
                    <Logo />
                    <span className='sr-only'> home</span>
                </a>

                <ul className='hidden items-center gap-0.5 lg:flex'>
                    {NAV_ITEMS.map(item => (
                        <li key={item.label}>
                            <a
                                href={item.href}
                                {...(item.external ? externalProps : {})}
                                className='inline-flex h-9 items-center gap-1 rounded-lg px-3 text-sm text-steel transition-colors hover:bg-white/[0.05] hover:text-bone'
                            >
                                {item.label}
                                {item.external && <ArrowUpRight aria-hidden='true' className='size-3.5' />}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-1.5'>
                    <a
                        href={LINKS.signIn}
                        {...externalProps}
                        className='hidden h-9 items-center rounded-lg px-3 text-sm text-steel transition-colors hover:text-bone sm:inline-flex'
                    >
                        Sign in
                    </a>

                    <a
                        href={LINKS.signUp}
                        {...externalProps}
                        className='inline-flex h-9 items-center gap-1.5 rounded-xl bg-ember px-3.5 text-sm font-medium text-ink transition-colors hover:bg-ember-soft'
                    >
                        Start free
                        <ArrowUpRight aria-hidden='true' className='size-4' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        className='grid size-9 place-items-center rounded-lg text-bone transition-colors hover:bg-white/[0.06] lg:hidden'
                    >
                        {open ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}
                        <span className='sr-only'>{open ? 'Close menu' : 'Open menu'}</span>
                    </button>
                </div>
            </nav>

            <div
                id='mobile-menu'
                hidden={!open}
                className='mx-auto mt-2 max-w-[1320px] rounded-2xl border border-white/10 bg-ink/95 p-2 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95)] backdrop-blur-xl lg:hidden'
            >
                <ul className='grid gap-0.5'>
                    {NAV_ITEMS.map((item, index) => (
                        <li key={item.label}>
                            <a
                                href={item.href}
                                onClick={() => setOpen(false)}
                                {...(item.external ? externalProps : {})}
                                className='flex h-12 items-center justify-between rounded-xl px-4 text-lg text-bone transition-colors hover:bg-white/[0.05]'
                            >
                                {item.label}
                                {item.external
                                    ? <ArrowUpRight aria-hidden='true' className='size-4 text-steel' />
                                    : <span aria-hidden='true' className='font-mono text-[10px] text-steel'>{pad(index + 1)}</span>}
                            </a>
                        </li>
                    ))}

                    <li className='sm:hidden'>
                        <a
                            href={LINKS.signIn}
                            {...externalProps}
                            className='flex h-12 items-center justify-between rounded-xl px-4 text-lg text-steel transition-colors hover:bg-white/[0.05] hover:text-bone'
                        >
                            Sign in
                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </a>
                    </li>
                </ul>
            </div>
        </header>
    )
}

export default SiteNav
